/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import type { DecoratorFunction } from '@veeam/components/lib/Toolbar/helpers/toolbarItemDecorators';
import type { Decorator, ToolbarCanAction } from '@veeam/components/lib/Toolbar/types';

import type { CustomToolbarPayload } from '../../components/Toolbar/interfaces';

type ValueType = number | string | boolean;
type ValuesType = ValueType[];

export const getPropertyValueByPath = (obj: Object, propertyPath: string): ValueType => propertyPath.split('.').reduce((res, path) => (
    res !== undefined && res !== null && res[path] !== undefined && res[path] !== null ? res[path] : null
), obj);

export type CustomToolbarItemDecorator = (payload: CustomToolbarPayload, api?: { canAction: ToolbarCanAction; }) => Decorator;

const hideForSelectionWithoutPropValue = (prop: string, value: ValueType): DecoratorFunction =>
    ({ selectedItems, flags }: CustomToolbarPayload) => ({
        hidden: !flags?.isAllPageMode
            && (selectedItems.length > 0 ? selectedItems.every(item => getPropertyValueByPath(item, prop) !== value) : false),
    });

const disableForAllPageSelection = (): DecoratorFunction =>
    ({ flags }: CustomToolbarPayload) => ({
        disabled: flags?.isAllPageMode,
    });

const disableForSelectionWithPropValues = (prop: string, values: ValuesType): DecoratorFunction =>
    ({ selectedItems, flags }: CustomToolbarPayload) => ({
        disabled: !flags?.isAllPageMode
            && (selectedItems.length > 0 ? selectedItems.every(item => values.includes(getPropertyValueByPath(item, prop) as any)) : false),
    });

const disableForSelectionWithPropValueCallback = <T = {}>(
    prop: string,
    cb: (value: ValuesType | ValueType, item: T) => boolean,
): DecoratorFunction =>
        ({ selectedItems, flags }: CustomToolbarPayload) => ({
            disabled: !flags?.isAllPageMode
                && (selectedItems.length > 0 ? selectedItems.every(item => cb(getPropertyValueByPath(item, prop) as any, item as T)) : false),
        });

const disableForAnyOfSelectionWithPropValueCallback = <T = {}>(
    prop: string,
    cb: (value: ValuesType | ValueType, item: T) => boolean,
): DecoratorFunction =>
        ({ selectedItems, flags }: CustomToolbarPayload) => ({
            disabled: !flags?.isAllPageMode
                && (selectedItems.length > 0 ? selectedItems.some(item => cb(getPropertyValueByPath(item, prop) as any, item as T)) : false),
        });

const hideForFilterWithoutPropValue = (prop: string, value: ValueType): DecoratorFunction =>
    (payload: CustomToolbarPayload) => {
        if (payload.flags?.isAllPageMode) {
            return {
                hidden: false,
            };
        }

        const filters = payload?.filters || null;
        const filterValue = filters && filters[prop] !== undefined && filters[prop] !== null ? filters[prop] : null;

        if (Array.isArray(filterValue)) {
            return {
                hidden: filterValue.length > 0 ? !filterValue.includes(value) : false,
            };
        }

        return {
            hidden: filterValue !== null ? filterValue !== value : false,
        };
    };

/**
 * @private
 * @param decorators
 * @param prop
 */
const processDecoratorsByPriority = (decorators: CustomToolbarItemDecorator[], prop: string): CustomToolbarItemDecorator =>
    (payload: CustomToolbarPayload, ...args) => decorators.reduce((res, dec) => {
        if (res[prop]) {
            return res;
        }

        const propsFromDecorator = dec(payload, ...args) || {};

        return {
            [prop]: propsFromDecorator[prop] === true,
        };
    }, { [prop]: false });

const processDecoratorsForHiddenByPriority = (decorators: CustomToolbarItemDecorator[]): CustomToolbarItemDecorator =>
    processDecoratorsByPriority(decorators, 'hidden');

const processDecoratorsForDisabledByPriority = (decorators: CustomToolbarItemDecorator[]): CustomToolbarItemDecorator =>
    processDecoratorsByPriority(decorators, 'disabled');

export {
    hideForSelectionWithoutPropValue,
    disableForAllPageSelection,
    disableForSelectionWithPropValues,
    disableForSelectionWithPropValueCallback,
    hideForFilterWithoutPropValue,
    processDecoratorsForHiddenByPriority,
    processDecoratorsForDisabledByPriority,
    disableForAnyOfSelectionWithPropValueCallback,
};
